<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore, useMapGetter } from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SectionLayout from '../../../../components-next/SectionLayout.vue';
import LucideIcon from 'dashboard/components/LucideIcon.vue';

// New split components
import AgentSettingTable from './AgentSettingTable.vue';
import AgentSettingList from './AgentSettingList.vue';

const PER_PAGE = 15;

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});

const agentList = computed(() => getters['agents/getAgents'].value);
const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');
const meta = useMapGetter('agents/getMeta');

// desktop uses server pagination; mobile uses infinite scroll
const currentPage = computed(() => meta.value?.currentPage || 1);
const hasMore = computed(() => {
  const total = meta.value?.count || 0;
  const perPage = meta.value?.perPage || PER_PAGE;
  const totalPages = Math.ceil(total / perPage);
  return currentPage.value < totalPages;
});

onMounted(() => {
  store.dispatch('agents/get', { page: 1, perPage: PER_PAGE });
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) return [];
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => currentUserId.value !== agent.id;

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = (message, options = { type: 'info' }) => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message, options);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'), {
      type: 'success',
    });
  } catch {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'), {
      type: 'error',
    });
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};

// desktop page change
const onPageChange = page => {
  store.dispatch('agents/get', { page, perPage: meta.value.perPage || PER_PAGE });
};

// mobile/tablet infinite scroll
const loadMoreAgents = async () => {
  if (uiFlags.value.isFetching || uiFlags.value.isFetchingMore || !hasMore.value) return;
  await store.dispatch('agents/getMore', {
    page: currentPage.value + 1,
    perPage: meta.value.perPage || PER_PAGE,
  });
};
</script>

<template>
  <SectionLayout :title="$t('AGENT_MGMT.HEADER')">
    <template #header-left>
      <button
        class="rounded-lg input-base !bg-grass-500 flex items-center gap-2 text-white text-sm font-semibold"
        @click="openAddPopup"
      >
        {{ $t('AGENT_MGMT.HEADER_BTN_TXT') }}
        <LucideIcon name="plus"/>
      </button>
    </template>

    <SettingsLayout
      :is-loading="uiFlags.isFetching"
      :loading-message="$t('AGENT_MGMT.LOADING')"
      :no-records-found="!agentList.length"
      :no-records-message="$t('AGENT_MGMT.LIST.404')"
    >
      <template #header>
        <BaseSettingsHeader
          :description="$t('AGENT_MGMT.DESCRIPTION')"
          :link-text="$t('AGENT_MGMT.LEARN_MORE')"
          feature-name="agents"
        />
      </template>

      <template #body>
        <!-- Desktop Table + Pagination -->
        <div class="hidden lg:block">
          <AgentSettingTable
            :agents="agentList"
            :meta="meta"
            :show-edit-action="showEditAction"
            :show-delete-action="showDeleteAction"
            :get-agent-role-name="getAgentRoleName"
            :get-agent-role-permissions="getAgentRolePermissions"
            :loading="loading"
            @edit="openEditPopup"
            @delete="openDeletePopup"
            @pageChange="onPageChange"
          />
        </div>

        <!-- Mobile/Tablet Infinite Scroll -->
        <div class="block lg:hidden">
          <AgentSettingList
            :agents="agentList"
            :is-loading-more="uiFlags.isFetchingMore"
            :has-more="hasMore"
            :show-edit-action="showEditAction"
            :show-delete-action="showDeleteAction"
            :loading="loading"
            @edit="openEditPopup"
            @delete="openDeletePopup"
            @loadMore="loadMoreAgents"
          />
        </div>
      </template>

      <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
        <AddAgent @close="hideAddPopup" />
      </woot-modal>

      <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
        <EditAgent
          v-if="showEditPopup"
          :id="currentAgent.id"
          :name="currentAgent.name"
          :type="currentAgent.role"
          :email="currentAgent.email"
          :availability="currentAgent.availability_status"
          :custom-role-id="currentAgent.custom_role_id"
          @close="hideEditPopup"
        />
      </woot-modal>

      <woot-delete-modal
        v-model:show="showDeletePopup"
        :on-close="closeDeletePopup"
        :on-confirm="confirmDeletion"
        :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
        :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
        :message-value="`${currentAgent.name}?`"
        :confirm-text="`${$t('AGENT_MGMT.DELETE.CONFIRM.YES')}`"
        :reject-text="`${$t('AGENT_MGMT.DELETE.CONFIRM.NO')}`"
      />
    </SettingsLayout>
  </SectionLayout>
</template>